import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { CheckCircle, XCircle, MapPin, Eye } from "lucide-react";
import { API } from "../../apiConfig";


const SalesAgentsTab = ({ agents, token, onRefresh }) => {
  const [territoryDialogOpen, setTerritoryDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [territoryForm, setTerritoryForm] = useState({ territory: "", postcodes: "" });

  const headers = { Authorization: `Bearer ${token}` };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const approveAgent = async (agentId) => {
    try {
      await axios.put(`${API}/admin/sales-agents/${agentId}/approve`, {}, { headers });
      toast.success("Sales agent approved");
      onRefresh();
    } catch (error) {
      toast.error("Failed to approve agent");
    }
  };

  const suspendAgent = async (agentId) => {
    try {
      await axios.put(`${API}/admin/sales-agents/${agentId}/suspend`, {}, { headers });
      toast.success("Sales agent suspended");
      onRefresh();
    } catch (error) {
      toast.error("Failed to suspend agent");
    }
  };

  const openTerritoryDialog = (agent) => {
    setSelectedAgent(agent);
    setTerritoryForm({ 
      territory: agent.territory || "", 
      postcodes: agent.postcodes?.join(", ") || "" 
    });
    setTerritoryDialogOpen(true);
  };

  const saveTerritoryAssignment = async () => {
    if (!selectedAgent) return;
    
    try {
      await axios.put(
        `${API}/admin/sales-agents/${selectedAgent.agent_id}/territory`,
        { 
          territory: territoryForm.territory,
          postcodes: territoryForm.postcodes.split(",").map(p => p.trim()).filter(Boolean)
        },
        { headers }
      );
      toast.success("Territory assigned successfully");
      setTerritoryDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error("Failed to assign territory");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'suspended':
        return <Badge className="bg-red-500">Suspended</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <>
      <Card className="rounded-2xl" data-testid="sales-agents-tab">
        <CardHeader>
          <CardTitle>Sales Agent Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Territory</TableHead>
                <TableHead>Linked Accounts</TableHead>
                <TableHead>Total Sales</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No sales agents yet
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => (
                  <TableRow key={agent.agent_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{agent.user?.name || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">{agent.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="bg-muted px-2 py-1 rounded text-sm">
                        {agent.agent_code}
                      </code>
                    </TableCell>
                    <TableCell>
                      {agent.territory || <span className="text-muted-foreground">Not assigned</span>}
                    </TableCell>
                    <TableCell>{agent.linked_accounts || 0}</TableCell>
                    <TableCell>{formatPrice(agent.total_sales)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{formatPrice(agent.total_commission)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatPrice(agent.pending_commission)} pending
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(agent.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {agent.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => approveAgent(agent.agent_id)}
                            title="Approve Agent"
                          >
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          </Button>
                        )}
                        {agent.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => suspendAgent(agent.agent_id)}
                            title="Suspend Agent"
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openTerritoryDialog(agent)}
                          title="Assign Territory"
                        >
                          <MapPin className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/admin/agents/${agent.agent_id}`, '_blank')}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Territory Assignment Dialog */}
      <Dialog open={territoryDialogOpen} onOpenChange={setTerritoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Territory</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedAgent && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="font-medium">{selectedAgent.user?.name}</p>
                <p className="text-sm text-muted-foreground">Code: {selectedAgent.agent_code}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Territory Name</label>
              <Input
                value={territoryForm.territory}
                onChange={(e) => setTerritoryForm({ ...territoryForm, territory: e.target.value })}
                placeholder="e.g., Sydney Metro, Northern NSW"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Postcodes (comma separated)</label>
              <Input
                value={territoryForm.postcodes}
                onChange={(e) => setTerritoryForm({ ...territoryForm, postcodes: e.target.value })}
                placeholder="2000, 2010, 2020"
              />
              <p className="text-xs text-muted-foreground">
                Optional: Restrict agent to specific postcodes
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerritoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTerritoryAssignment}>Save Territory</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesAgentsTab;
